/* eslint-disable */
import React, { useState, useEffect, useMemo } from "react";
import useUserContext from "../../hooks/useUserContext";
import getPrintTransactions from "./getPrintTransactions";
import Pagination from "../../components/pagination/Pagination";
import notify from "../../services/toast";
import { CSVLink } from "react-csv";
import debounce from "lodash.debounce";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { FiCopy } from "react-icons/fi";

const MONTH_NAMES = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
];

function PharosPrintTransactionsData() {
    const { userState } = useUserContext();

    const [allTransactions, setAllTransactions] = useState([]);
    const [userMonthlySummary, setUserMonthlySummary] = useState([]);
    const [availableMonths, setAvailableMonths] = useState([]);
    const [selectedMonth, setSelectedMonth] = useState(null);
    const [copied, setCopied] = React.useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [summarySearchTerm, setSummarySearchTerm] = useState("");
    const [sortKey, setSortKey] = useState(null);
    const [sortOrder, setSortOrder] = useState("asc");

    const [isMainLoading, setIsMainLoading] = useState(false);
    const [isSummaryLoading, setIsSummaryLoading] = useState(false);

    const [summaryPagination, setSummaryPagination] = useState({ limit: 10, page: 0, total: 0, remaining: 0 });

    // Client-side pagination state
    const [page, setPage] = useState(0);
    const limit = 10;

    // Fetch all transactions once on mount (or when token changes)
    useEffect(() => {
        if (userState?.accessToken) {
            fetchAllTransactions();
            fetchUserMonthlySummary(0);
        } else {
            notify("No access token available", "error");
        }
    }, [userState?.accessToken]);

    const fetchAllTransactions = async () => {
        setIsMainLoading(true);
        const response = await getPrintTransactions(userState.accessToken, userState._id, 0, 10000, "main");
        if (response.status === "success") {
            setAllTransactions(response.data.results || []);
            setPage(0);
        } else {
            notify("Failed to fetch all transactions", "error");
        }
        setIsMainLoading(false);
    };

    const fetchUserMonthlySummary = async (page = 0, selected = selectedMonth) => {
        setIsSummaryLoading(true);

        const response = await getPrintTransactions(
            userState.accessToken,
            userState._id,
            page,
            summaryPagination.limit,
            "summary",
            selected?.month,
            selected?.year
        );

        if (response.status === "success") {
            const {
                userMonthlySummary,
                totalUsers,
                limit,
                page: currentPage,
                remaining,
                availableMonths,
            } = response.data;

            setUserMonthlySummary(userMonthlySummary || []);
            setAvailableMonths(availableMonths || []);
            setSummaryPagination({
                total: totalUsers,
                limit,
                page: currentPage,
                remaining,
            });

        } else {
            notify(response.message || "Failed to fetch summary", "error");
        }

        setIsSummaryLoading(false);
    };

    const handleSearch = useMemo(
        () =>
            debounce((val) => {
                setSearchTerm(val.toLowerCase());
                setPage(0); // Reset to first page on new search
            }, 300),
        []
    );

    const handleMonthTabClick = (year, month) => {
        const selected = { year, month };
        setSelectedMonth(selected);
        fetchUserMonthlySummary(0, selected); // Don't setSummaryPagination manually
    };


    // Filtered and sorted transactions based on full dataset
    const filteredTransactions = useMemo(() => {
        let data = [...allTransactions];

        if (searchTerm) {
            data = data.filter(
                (tx) =>
                    tx.fullName?.toLowerCase().includes(searchTerm) ||
                    tx.emailAddress?.toLowerCase().includes(searchTerm)
            );
        }

        if (sortKey) {
            data.sort((a, b) => {
                let valA = a[sortKey];
                let valB = b[sortKey];

                // Handle strings and numbers safely
                if (typeof valA === "string") valA = valA.toLowerCase();
                if (typeof valB === "string") valB = valB.toLowerCase();

                if (valA > valB) return sortOrder === "asc" ? 1 : -1;
                if (valA < valB) return sortOrder === "asc" ? -1 : 1;
                return 0;
            });
        }

        return data;
    }, [allTransactions, searchTerm, sortKey, sortOrder]);

    // Slice for current page
    const paginatedTransactions = useMemo(() => {
        const start = page * limit;
        return filteredTransactions.slice(start, start + limit);
    }, [filteredTransactions, page, limit]);

    // Monthly summary filtered by selected month
    const filteredMonthlySummary = selectedMonth
        ? userMonthlySummary.filter(
            (user) =>
                user._id.year === selectedMonth.year && user._id.month === selectedMonth.month
        )
        : [];

    const mainHeaders = ["Date", "User", "Email", "Site", "Pages", "Color", "Mono", "Cost"];

    function CopyEmailButton({ email }) {
        const [copied, setCopied] = React.useState(false);

        const handleCopy = async () => {
            try {
                await navigator.clipboard.writeText(email);
                setCopied(true);
                setTimeout(() => setCopied(false), 1500); // Reset after 1.5s
            } catch (err) {
                console.error("Clipboard copy failed:", err);
            }
        };

        return (
            <button
                onClick={handleCopy}
                className="ml-2 text-gray-500 hover:text-gray-800 flex items-center gap-1"
                title="Copy Email"
            >
                <FiCopy className="inline text-lg" />
                {copied && <span className="text-xs text-green-600">Copied!</span>}
            </button>
        );
    }

    return (
       <div className="bg-gray-100 rounded-3xl shadow-xl p-6 sm:p-8 space-y-8">

            {/* -------- Transactions Table Card -------- */}
            <div className="bg-white rounded-3xl shadow-xl p-6 sm:p-8 space-y-4">
                <div className="flex justify-between items-center mb-4 flex-wrap gap-2">
                    <h2 className="text-3xl font-semibold">Print Transactions</h2>
                    <div className="flex gap-2 flex-wrap">
                        <CSVLink
                            data={paginatedTransactions}
                            filename="transactions-current-page.csv"
                            className="bg-success-800 text-white px-3 py-2 rounded"
                        >
                            Export Current Page
                        </CSVLink>

                        <CSVLink
                            data={filteredTransactions}
                            filename="transactions-filtered.csv"
                            className={`bg-primary-400 text-white px-3 py-2 rounded ${!searchTerm.trim() ? "opacity-50 cursor-not-allowed" : ""}`}
                            onClick={(e) => {
                                if (!searchTerm.trim()) e.preventDefault();
                            }}
                        >
                            Export Search Results
                        </CSVLink>

                        <CSVLink
                            data={allTransactions}
                            filename="transactions-all.csv"
                            className="bg-secondary-600 text-white px-3 py-2 rounded"
                        >
                            Export Complete Data
                        </CSVLink>
                    </div>
                </div>

                <input
                    type="text"
                    placeholder="Search name or email..."
                    onChange={(e) => handleSearch(e.target.value)}
                    className="w-full border border-primary-400 focus:border-primary-400 focus:outline-none focus:ring-1 focus:ring-primary-400 p-2 mb-4 rounded"
                />

                <div className="mb-2 text-sm text-gray-700">
                    Showing {paginatedTransactions.length} of {filteredTransactions.length} results
                </div>

                <div className="overflow-auto rounded-lg shadow border border-gray-200 mb-6">
                    <table className="min-w-full table-auto">
                        <thead className="bg-gray-50 text-gray-700 text-sm font-semibold sticky top-0 z-10 border-b border-gray-300">
                            <tr>
                                {mainHeaders.map((title, i) => (
                                    <th key={i} scope="col" className="px-4 py-3 text-left whitespace-nowrap select-none">
                                        {title}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody className="text-sm divide-y divide-gray-200">
                            {isMainLoading
                                ? Array.from({ length: 5 }).map((_, idx) => (
                                    <tr key={idx} className="animate-pulse">
                                        {Array.from({ length: 10 }).map((_, i) => (
                                            <td key={i} className="px-4 py-3"><Skeleton /></td>
                                        ))}
                                    </tr>
                                ))
                                : paginatedTransactions.length === 0
                                    ? (
                                        <tr>
                                            <td colSpan={10} className="text-center py-8 text-gray-500 flex flex-col items-center justify-center gap-2">
                                                <svg className="w-12 h-12 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-6a4 4 0 118 0v6M5 17h14" />
                                                </svg>
                                                No transactions found.
                                                <span className="text-xs text-gray-400">Try adjusting your filters or search terms.</span>
                                            </td>
                                        </tr>
                                    )
                                    : paginatedTransactions.map((tx, idx) => (
                                        <tr key={idx} tabIndex={0} className="hover:bg-gray-50 focus:bg-gray-100 focus:outline-none transition-colors">
                                            <td className="px-4 py-3 whitespace-nowrap">{new Date(tx.transactionDate).toLocaleString()}</td>
                                            <td className="px-4 py-3">{tx.fullName || tx.userName || "N/A"}</td>
                                            <td className="px-4 py-3">{tx.emailAddress}</td>
                                            <td className="px-4 py-3">{tx.site}</td>
                                            <td className="px-4 py-3">{tx.totalPages}</td>
                                            <td className="px-4 py-3">{tx.colorPages}</td>
                                            <td className="px-4 py-3">{tx.monoPages}</td>
                                            <td className="px-4 py-3">${(tx.totalCost || 0).toFixed(2)}</td>
                                        </tr>
                                    ))}
                        </tbody>
                    </table>
                </div>

                <Pagination
                    pagination={{
                        page,
                        limit,
                        total: filteredTransactions.length,
                        remaining: filteredTransactions.length - (page + 1) * limit,
                    }}
                    prev={() => setPage((prev) => Math.max(prev - 1, 0))}
                    next={() => setPage((prev) => ((page + 1) * limit < filteredTransactions.length ? prev + 1 : prev))}
                    disablePrev={page <= 0}
                    disableNext={(page + 1) * limit >= filteredTransactions.length}
                />
            </div>

            {/* -------- Monthly Summary Table Card -------- */}
            <div className="bg-white rounded-3xl shadow-xl p-6 sm:p-8 space-y-4">
                <h3 className="text-2xl font-semibold text-center mb-4">Monthly Summary by User</h3>

                {/* Month Tabs */}
                <div className="flex flex-wrap justify-center mb-4">
                    {availableMonths.map((monthData, idx) => (
                        <button
                            key={idx}
                            className={`px-4 py-2 m-1 rounded transition-all ${selectedMonth?.year === monthData.year && selectedMonth?.month === monthData.month
                                    ? "bg-primary-400 text-white"
                                    : "bg-gray-200 hover:bg-gray-300"
                                }`}
                            onClick={() => handleMonthTabClick(monthData.year, monthData.month)}
                        >
                            {`${MONTH_NAMES[monthData.month - 1]} ${monthData.year}`}
                        </button>
                    ))}
                </div>

                {/* Search */}
                <div className="mb-4 max-w-md mx-auto">
                    <input
                        type="text"
                        placeholder="Search user or email in summary..."
                        onChange={(e) => setSummarySearchTerm(e.target.value.toLowerCase())}
                        className="w-full border border-primary-400 focus:border-primary-400 focus:outline-none focus:ring-1 focus:ring-primary-400 p-2 mb-4 rounded"
                    />
                </div>

                <div className="overflow-auto rounded-lg shadow border border-gray-200 mt-4">
                    <table className="min-w-full table-auto">
                        <thead className="bg-gray-50 text-gray-700 text-sm font-semibold sticky top-0 z-10">
                            <tr>
                                <th className="px-4 py-2 text-left">Name</th>
                                <th className="px-4 py-2 text-left">Email</th>
                                <th className="px-4 py-2 text-left">Site</th>
                                <th className="px-4 py-2 text-left">Total Pages</th>
                                <th className="px-4 py-2 text-left">Color Pages</th>
                                <th className="px-4 py-2 text-left">Mono Pages</th>
                                <th className="px-4 py-2 text-left">Cost</th>
                            </tr>
                        </thead>
                        <tbody className="text-sm divide-y divide-gray-200">
                            {isSummaryLoading
                                ? Array.from({ length: 5 }).map((_, idx) => (
                                    <tr key={idx}>
                                        {Array.from({ length: 7 }).map((_, cellIdx) => (
                                            <td key={cellIdx} className="px-4 py-2"><Skeleton /></td>
                                        ))}
                                    </tr>
                                ))
                                : filteredMonthlySummary.length === 0
                                    ? (
                                        <tr>
                                            <td colSpan={7} className="text-center py-4 text-gray-500">No summary data for selected month.</td>
                                        </tr>
                                    )
                                    : filteredMonthlySummary
                                        .filter(
                                            (user) =>
                                                user.fullName?.toLowerCase().includes(summarySearchTerm) ||
                                                user.emailAddress?.toLowerCase().includes(summarySearchTerm)
                                        )
                                        .map((user, idx) => (
                                            <tr key={idx} className={`hover:bg-gray-50 transition-colors ${user.totalMonthlyCost > 50 ? "bg-yellow-100" : ""}`}>
                                                <td className="px-4 py-2">{user.fullName}</td>
                                                <td className="px-4 py-2 flex items-center">{user.emailAddress}<CopyEmailButton email={user.emailAddress} /></td>
                                                <td className="px-4 py-2">{user.site}</td>
                                                <td className="px-4 py-2">{user.totalMonthlyPages}</td>
                                                <td className="px-4 py-2">{user.totalMonthlyColorPages}</td>
                                                <td className="px-4 py-2">{user.totalMonthlyMonoPages}</td>
                                                <td className="px-4 py-2 font-medium text-green-600">${user.totalMonthlyCost?.toFixed(2) || "0.00"}</td>
                                            </tr>
                                        ))}
                        </tbody>
                    </table>
                </div>

                <Pagination
                    pagination={summaryPagination}
                    prev={() => fetchUserMonthlySummary(summaryPagination.page - 1)}
                    next={() => fetchUserMonthlySummary(summaryPagination.page + 1)}
                    disablePrev={summaryPagination.page <= 0}
                    disableNext={summaryPagination.remaining <= 0}
                />
            </div>

        </div>
    );
}

export default PharosPrintTransactionsData;
